import { type CodecEventRecord, CodecEventType, type CodecMeshDeviceEventRecord } from "@irriga-mais/common";
import { atom } from "jotai";
import { apiService } from "@/api";
import {
  type CurrentLICPacket,
  isStatusPacketPayload,
  type LICPacketPayloadType,
} from "@/api/model/current-lic-packet";
import type { LICStateProperties, LicState } from "@/api/model/lic-state";
import type { AUTPropertyDevice } from "@/api/queries/account";
import { loadCurrentLICPacketsForDevices } from "@/api/queries/current-lic-packet";
import type { DirectusApiService } from "@/api/service";
import { isLICDeviceModel } from "@/utils/device-model";
import {
  type DeviceState,
  findEquipmentIdByOrdIdx,
  getDeviceState,
  type LicVirtualDeviceEquipment,
  type LicVirtualDeviceEquipmentDetails,
} from "@/utils/lic-state";
import { TimeoutTimer } from "@/utils/timer";
import { withOperationHandlingAtom } from "../operations";
import { DATA_FETCH_INTERVAL_MILLIS } from "./constants";
import {
  licDevicesAtom,
  licMappedDevicesAtom,
  propertyDevicesAtom,
  selectedPropertyAtom,
  selectedPropertyIdAtom,
} from "./property-domain";

export const currentLICPacketsAtom = atom<CurrentLICPacket[]>([]);
currentLICPacketsAtom.debugLabel = "currentLICPacketsAtom";

export const currentLICPacketsLoadingAtom = atom<boolean>(false);
currentLICPacketsLoadingAtom.debugLabel = "currentLICPacketsLoadingAtom";
export const currentLICPacketsErrorAtom = atom<string | null>(null);
currentLICPacketsErrorAtom.debugLabel = "currentLICPacketsErrorAtom";

export const currentLICPacketsByPropertyAtom = atom<CurrentLICPacket[]>((get) => {
  const packets = get(currentLICPacketsAtom);
  const licDevices = get(licDevicesAtom);
  if (!licDevices || licDevices.length === 0) return [];
  const licIds = new Set(licDevices.map((d) => d.id));
  return packets.filter((p) => licIds.has(p.device as unknown as string));
});
currentLICPacketsByPropertyAtom.debugLabel = "currentLICPacketsByPropertyAtom";

export const currentLICPacketByIdAtom = atom((get) => (id: number): CurrentLICPacket | null => {
  const packets = get(currentLICPacketsAtom);
  return packets.find((p) => p.id === id) || null;
});
currentLICPacketByIdAtom.debugLabel = "currentLICPacketByIdAtom";

export const latestLICPacketByDeviceAndTypeAtom = atom(
  (get) =>
    (deviceId: string, payloadType: CurrentLICPacket["payload_type"]): CurrentLICPacket | null => {
      const packets = get(currentLICPacketsAtom);
      const filtered = packets.filter(
        (p): p is CurrentLICPacket => (p.device as unknown as string) === deviceId && p.payload_type === payloadType
      );
      return filtered.sort((a, b) => b.packet_date.localeCompare(a.packet_date))[0] || null;
    }
);
latestLICPacketByDeviceAndTypeAtom.debugLabel = "latestLICPacketByDeviceAndTypeAtom";

export const fetchCurrentLICPacketsAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    console.log("fetchCurrentLICPacketsAtom - No property ID selected");
    return;
  }

  const licDevices = get(licDevicesAtom);
  const deviceIds = licDevices.map((d) => d.id);
  if (deviceIds.length === 0) {
    set(currentLICPacketsAtom, []);
    return;
  }

  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchCurrentLICPackets",
      message: "Carregando pacotes LIC...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(currentLICPacketsLoadingAtom, true);
      set(currentLICPacketsErrorAtom, null);
      try {
        const packets = await loadCurrentLICPacketsForDevices(apiService, deviceIds);
        set(currentLICPacketsAtom, packets);
        return packets;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        set(currentLICPacketsErrorAtom, msg);
        console.error("Failed to fetch current LIC packets:", error);
        throw error;
      } finally {
        set(currentLICPacketsLoadingAtom, false);
      }
    }
  );
});
fetchCurrentLICPacketsAtom.debugLabel = "fetchCurrentLICPacketsAtom";
fetchCurrentLICPacketsAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1200);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};

export const licStatesAtom = atom<LicState<{ device: string }>[]>([]);
licStatesAtom.debugLabel = "licStatesAtom";

export const licStatesLoadingAtom = atom<boolean>(false);
licStatesLoadingAtom.debugLabel = "licStatesLoadingAtom";
export const licStatesErrorAtom = atom<string | null>(null);
licStatesErrorAtom.debugLabel = "licStatesErrorAtom";

export const licStatesByPropertyAtom = atom<LicState[]>((get) => {
  const states = get(licStatesAtom);
  const licDevices = get(licDevicesAtom);
  if (!licDevices || licDevices.length === 0) return [];
  const licIds = new Set(licDevices.map((d) => d.id));
  return states.filter((s) => licIds.has(s.device as unknown as string));
});
licStatesByPropertyAtom.debugLabel = "licStatesByPropertyAtom";

export const licStateByDeviceIdAtom = atom((get) => (deviceId: string): LicState<{ device: string }> | null => {
  const states = get(licStatesAtom);
  return states.find((s) => (s.device as unknown as string) === deviceId) || null;
});
licStateByDeviceIdAtom.debugLabel = "licStateByDeviceIdAtom";

export const fetchLicStatesAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    console.log("fetchLicStatesAtom - No property ID selected");
    return;
  }

  const licDevices = get(licDevicesAtom);
  const deviceIds = licDevices.map((d) => d.id);
  if (deviceIds.length === 0) {
    set(licStatesAtom, []);
    return;
  }

  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchLicStates",
      message: "Carregando estado LIC...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(licStatesLoadingAtom, true);
      set(licStatesErrorAtom, null);
      try {
        const states = await apiService.licState.query({
          fields: ["*"],
          filter: {
            device: { _in: deviceIds },
          } as any,
          sort: ["-date_updated"],
        });
        set(licStatesAtom, states as any);
        return states;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        set(licStatesErrorAtom, msg);
        console.error("Failed to fetch LIC states:", error);
        throw error;
      } finally {
        set(licStatesLoadingAtom, false);
      }
    }
  );
});
fetchLicStatesAtom.debugLabel = "fetchLicStatesAtom";
fetchLicStatesAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1300);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};

export const equipmentsStateAtom = atom<{ [key: string]: DeviceState }>((get) => {
  const licStates = get(licStatesByPropertyAtom);
  const latestLICPacketByDeviceAndType = get(latestLICPacketByDeviceAndTypeAtom);
  return licStates.reduce(
    (result, state) => {
      const devices = state.devices as LICStateProperties["devices"];
      const statusPackage = latestLICPacketByDeviceAndType(state.device as string, "status");
      const status = statusPackage
        ? isStatusPacketPayload(statusPackage.payload_data)
          ? statusPackage.payload_data.status
          : false
        : false;
      if (devices && status) {
        devices.forEach((device) => {
          if (device.elementId) {
            const eqState = getDeviceState(device.ord_idx, status);
            result[device.elementId] = eqState;
          }
        });
      }
      return result;
    },
    {} as { [key: string]: DeviceState }
  );
});
equipmentsStateAtom.debugLabel = "equipmentsStateAtom";

export const latestLICEventsAtom = atom<CodecEventRecord<{ dateType: string; eventStateType: string }>[]>([]);
latestLICEventsAtom.debugLabel = "latestLICEventsAtom";

export const latestLICEventsLoadingAtom = atom<boolean>(false);
latestLICEventsLoadingAtom.debugLabel = "latestLICEventsLoadingAtom";
export const latestLICEventsErrorAtom = atom<string | null>(null);
latestLICEventsErrorAtom.debugLabel = "latestLICEventsErrorAtom";

async function loadLatestLICEventsForDevices(apiService: DirectusApiService, deviceIds: string[]) {
  try {
    const minEventDateOffset = 7 * 24 * 60 * 60 * 1000;
    const jobs = deviceIds.map((deviceId) =>
      apiService.codecEvent.readLatestItemsByCodecId({
        id: deviceId,
        start_date: new Date(Date.now() - minEventDateOffset),
      })
    );
    const results = await Promise.all(jobs);
    const events = results.flat() as CodecEventRecord<{ dateType: string; eventStateType: string }>[];
    return events as CodecEventRecord<{ dateType: string; eventStateType: string }>[];
  } catch (error) {
    console.error("Error loading latest LIC events:", error);
    throw error;
  }
}

export const fetchLatestLICEventsAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    console.log("fetchLatestLICEventsAtom - No property ID selected");
    return;
  }

  const licDevices = get(licDevicesAtom);
  const deviceIds = licDevices.map((d) => d.id);
  if (deviceIds.length === 0) {
    set(latestLICEventsAtom, []);
    return;
  }

  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchLatestLICEvents",
      message: "Carregando eventos LIC...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(latestLICEventsLoadingAtom, true);
      set(latestLICEventsErrorAtom, null);
      try {
        const events = await loadLatestLICEventsForDevices(apiService, deviceIds);
        set(latestLICEventsAtom, events);
        return events;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        set(latestLICEventsErrorAtom, msg);
        console.error("Failed to fetch latest LIC events:", error);
        throw error;
      } finally {
        set(latestLICEventsLoadingAtom, false);
      }
    }
  );
});
fetchLatestLICEventsAtom.debugLabel = "fetchLatestLICEventsAtom";
fetchLatestLICEventsAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1200);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};

// Mesh Device Event atoms
export const latestMeshDeviceEventsAtom = atom<
  CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: string }>[]
>([]);
latestMeshDeviceEventsAtom.debugLabel = "latestMeshDeviceEventsAtom";

export const latestMeshDeviceEventsLoadingAtom = atom<boolean>(false);
latestMeshDeviceEventsLoadingAtom.debugLabel = "latestMeshDeviceEventsLoadingAtom";
export const latestMeshDeviceEventsErrorAtom = atom<string | null>(null);
latestMeshDeviceEventsErrorAtom.debugLabel = "latestMeshDeviceEventsErrorAtom";

async function loadLatestMeshDeviceEventsForDevices(apiService: DirectusApiService, deviceIds: string[]) {
  try {
    const minEventDateOffset = 7 * 24 * 60 * 60 * 1000;
    const jobs = deviceIds.map((deviceId) =>
      apiService.codecMeshDeviceEvent.readLatestItemsByCodecId({
        id: deviceId,
        start_date: new Date(Date.now() - minEventDateOffset),
      })
    );
    const results = await Promise.all(jobs);
    const events = results.flat() as CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: string }>[];
    return events as CodecMeshDeviceEventRecord<{ dateType: string; eventStateType: string }>[];
  } catch (error) {
    console.error("Error loading latest mesh device events:", error);
    throw error;
  }
}

export const fetchLatestMeshDeviceEventsAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    console.log("fetchLatestMeshDeviceEventsAtom - No property ID selected");
    return;
  }

  const licDevices = get(licDevicesAtom);
  const deviceIds = licDevices.map((d) => d.id);
  if (deviceIds.length === 0) {
    set(latestMeshDeviceEventsAtom, []);
    return;
  }

  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchLatestMeshDeviceEvents",
      message: "Carregando eventos de dispositivos mesh...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(latestMeshDeviceEventsLoadingAtom, true);
      set(latestMeshDeviceEventsErrorAtom, null);
      try {
        const events = await loadLatestMeshDeviceEventsForDevices(apiService, deviceIds);
        set(latestMeshDeviceEventsAtom, events);
        return events;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        set(latestMeshDeviceEventsErrorAtom, msg);
        console.error("Failed to fetch latest mesh device events:", error);
        throw error;
      } finally {
        set(latestMeshDeviceEventsLoadingAtom, false);
      }
    }
  );
});
fetchLatestMeshDeviceEventsAtom.debugLabel = "fetchLatestMeshDeviceEventsAtom";
fetchLatestMeshDeviceEventsAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1200);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};

export type LICConsolidateData = {
  lic: AUTPropertyDevice;
  mappedDevices: AUTPropertyDevice[];
  currentState: LicState | null;
  currentPackets: {
    [payloadType in LICPacketPayloadType]?: CurrentLICPacket<payloadType>;
  };
  virtualDevices: Array<{
    equipment: LicVirtualDeviceEquipmentDetails | null;
    state: DeviceState | null;
    virtualDevice: LicVirtualDeviceEquipment["virtualDevice"];
  }>;
  virtualDeviceStateCount: {
    [k in keyof DeviceState]: number;
  };
  latestPacket: CurrentLICPacket | null;
};

export const licConsolidatedDataAtom = atom((get) => {
  const propertyDevices = get(propertyDevicesAtom);
  const lics = propertyDevices.filter((pd) => isLICDeviceModel(pd.device.model));
  const licMappedDevices = get(licMappedDevicesAtom);
  const selectedProperty = get(selectedPropertyAtom);
  const states = get(licStatesAtom);
  const currentLICPacketsByProperty = get(currentLICPacketsByPropertyAtom);

  if (!selectedProperty) {
    return {};
  }

  const result: { [licDeviceId: string]: LICConsolidateData } = lics.reduce(
    (licAcc, lic) => {
      const licId = lic.device.id;
      const licState = states.find((s) => s.device === licId) || null;
      const licPackets = currentLICPacketsByProperty.filter((p) => p.device === licId);
      const packetTypeMapping = licPackets.reduce(
        (acc, p) => {
          acc[p.payload_type] = p as any;
          return acc;
        },
        {} as LICConsolidateData["currentPackets"]
      );
      /**
       * Sort key of a virtual device: the element display name, with tank fertigation
       * devices grouped under the fixed "Tanques" label.
       *
       * @param equipment - Resolved equipment of a virtual device.
       * @returns The name used to order the device list.
       */
      const getEquipmentSortName = (equipment: LicVirtualDeviceEquipmentDetails) => {
        if (equipment.elementType === "tank_ferti") {
          return "Tanques";
        }

        return "name" in equipment.element ? equipment.element.name : equipment.element.label;
      };

      const virtualDeviceList = ((licState?.devices as LICStateProperties["devices"]) || [])
        .map((dev) => {
          const eq = findEquipmentIdByOrdIdx(dev.ord_idx, licState!, selectedProperty) ?? null;
          const state = packetTypeMapping.status
            ? getDeviceState(dev.ord_idx, packetTypeMapping.status.payload_data.status)
            : null;
          return {
            equipment: eq,
            state,
            virtualDevice: dev,
          };
        })
        .sort((a, b) => {
          if (!a.equipment || !b.equipment) return 0;

          const nameA = getEquipmentSortName(a.equipment);
          const nameB = getEquipmentSortName(b.equipment);

          return nameA.localeCompare(nameB, "pt-BR", {
            numeric: true,
            sensitivity: "base",
          });
        });
      const latestLicPacket = licPackets.toSorted((a, b) => b.packet_date.localeCompare(a.packet_date)).at(0);

      licAcc[licId] = {
        lic,
        mappedDevices: Array.from(licMappedDevices[licId] || [])
          .map((d) => propertyDevices.find((pd) => pd.device.id === d.id))
          .filter(Boolean) as AUTPropertyDevice[],
        currentState: licState,
        currentPackets: packetTypeMapping,
        virtualDevices: virtualDeviceList,
        virtualDeviceStateCount: virtualDeviceList.reduce(
          (countAcc, vd) => {
            if (vd.state) {
              countAcc.synced += vd.state.synced ? 1 : 0;
              countAcc.failed += vd.state.failed ? 1 : 0;
              countAcc.outputOn += vd.state.outputOn ? 1 : 0;
              countAcc.inputOn += vd.state.inputOn ? 1 : 0;
            }
            return countAcc;
          },
          {
            synced: 0,
            failed: 0,
            outputOn: 0,
            inputOn: 0,
          } as { [k in keyof DeviceState]: number }
        ),
        latestPacket: latestLicPacket ?? null,
      };
      return licAcc;
    },
    {} as { [licDeviceId: string]: LICConsolidateData }
  );
  return result;
});
licConsolidatedDataAtom.debugLabel = "licConsolidatedDataAtom";

/** Current sector valve output states, shared by pump and project controls. */
export const sectorValveStatusMapAtom = atom((get) => {
  const statuses = new Map<string, "on" | "off">();
  for (const lic of Object.values(get(licConsolidatedDataAtom))) {
    for (const device of lic.virtualDevices) {
      if (device.equipment?.elementType !== "sector" || !device.state) continue;
      const sector = device.equipment.element;
      const sectorId = sector.id || sector.metadata?.sectorId;
      if (sectorId) statuses.set(String(sectorId), device.state.outputOn ? "on" : "off");
    }
  }
  return statuses;
});
sectorValveStatusMapAtom.debugLabel = "sectorValveStatusMapAtom";
